import { FormikErrors } from 'formik';
import * as Yup from 'yup';
import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import type {
  CallSummaryPayload,
  CallTranscriptPayload,
  DisplayConfigItem,
  ResourceRequestSharePayload,
} from '@rsos/reduxDataStore/src/features/eventShare/types';
import type { NorfolkSouthernRequestResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentGeoLocation } from '@rsos/utils/getMostRecentLoc';
import { isValidEmail } from '@rsos/utils/validators';
import { LATEST_SUMMARY_INDEX } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionAdapter';
import type { TranscriptionStateCall } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionSlice.types';
import {
  CALL_RESOURCE,
  type Checkbox,
  type SelectedAlert,
  type ShareResource,
} from '../../constants/ShareEvent.types';
import dataSources, {
  DATA_SOURCE_IDS,
  resourceRequestDataSources,
} from '../../constants/dataSources';
import {
  CONSIST_SHARE_EXPIRY_SECONDS,
  MAP_DISPLAY_CONFIG,
  SHARE_EXPIRY_SECONDS,
} from '../../constants/shareEventDefaults';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_IAR_SHARE,
  type FieldSharingMethod,
} from '../../constants/shareMethods';
import { type MultiInputTag } from './ShareEventRecipients';

/** Data-source ids whose live data is pulled from the transcription slice.
 * Sourced from DATA_SOURCE_IDS so they can't drift from the checkbox ids. */
export const CALL_SUMMARY_SOURCE_ID = DATA_SOURCE_IDS.CALL_SUMMARY;
export const CALL_TRANSCRIPT_SOURCE_ID = DATA_SOURCE_IDS.CALL_TRANSCRIPT;

export interface IarSelectedMember {
  agency_id: number;
  member_id: number;
  name?: string;
  subscriber_name?: string;
}

export interface FieldFormValues {
  recipients: MultiInputTag[];
  sharingMethod: FieldSharingMethod;
  dataSources: Checkbox[];
  notes: string;
  iarSelectedMembers: IarSelectedMember[];
}

/**
 * The Field Share modal is a small wizard. Steps are identified by string keys
 * (not positional numbers) so the IaR-only `IAR_SELECT` step can sit between
 * `RECIPIENTS` and `DATA_SOURCES` without renumbering the rest. Order is defined
 * per sharing method by `getStepSequence`, not by the value of these keys.
 */
export const SHARE_STEP = {
  RECIPIENTS: 'recipients',
  IAR_SELECT: 'iar-select',
  DATA_SOURCES: 'data-sources',
  NOTES: 'notes',
  SHARE_LINK: 'share-link',
} as const;

export type StepId = (typeof SHARE_STEP)[keyof typeof SHARE_STEP];

export const getStepSequence = (method: FieldSharingMethod): StepId[] =>
  method === FIELD_IAR_SHARE
    ? [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.IAR_SELECT,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
      ]
    : [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
        SHARE_STEP.SHARE_LINK,
      ];

export const getNextStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : null;
};

export const getPrevStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx > 0 ? seq[idx - 1] : null;
};

/** Create the validation schema based on the current step and sharing method */
export const createValidationSchema = (
  currentStep: StepId,
  sharingMethod: FieldSharingMethod,
) => {
  // Data sources are only required once the wizard has reached the data-sources
  // step. Order varies by sharing method, so compare positions in the sequence.
  const sequence = getStepSequence(sharingMethod);
  const hasReachedDataSources =
    sequence.indexOf(currentStep) >= sequence.indexOf(SHARE_STEP.DATA_SOURCES);

  const baseValidation = {
    sharingMethod: Yup.string().required(),
    dataSources: Yup.array().test(
      'datasources-required',
      'Select at least one data source',
      function (value: Checkbox[] | undefined) {
        if (hasReachedDataSources) {
          return Boolean(
            value && value.some((source: Checkbox) => source.isChecked),
          );
        }
        return true;
      },
    ),
    sessions: Yup.array(),
    notes: Yup.string(),
  };

  // Different validation based on sharing method
  const recipientsValidation =
    sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
      ? Yup.array()
          .min(1, 'At least one recipient is required')
          .test(
            'recipients-valid',
            'Invalid email format',
            function (value: MultiInputTag[] | undefined) {
              if (value && value.length > 0) {
                return value.every(
                  (recipient: MultiInputTag) => recipient.isValid,
                );
              }
              return true;
            },
          )
          .test(
            'recipients-limit',
            'Limit reached: Total recipients cannot exceed 10.',
            function (value: MultiInputTag[] | undefined) {
              return !(value && value.length > 10);
            },
          )
      : Yup.array();

  return Yup.object().shape({
    ...baseValidation,
    recipients: recipientsValidation,
  });
};

export interface DataSourceOptions {
  /**
   * Whether Caller Data (ADR) is offered. Off by default: legal has restricted
   * sharing health-profile source data outside UNITE, so Caller Data stays
   * hidden unless the `field-share-caller-data` flag turns it back on.
   */
  includeCallerData?: boolean;
}

/**
 * Data sources offered for a resource. Alerts have no call audio, so the
 * transcription-backed sources are not offered for them. Caller Data is
 * omitted unless `includeCallerData` is set — an unoffered source can never be
 * checked, so it also stays out of the payload and display_config.
 */
export const getDataSourcesForResource = (
  resource: ShareResource,
  { includeCallerData = false }: DataSourceOptions = {},
): Checkbox[] => {
  if (resource.kind === 'resource_request') {
    return resourceRequestDataSources;
  }
  return dataSources.filter(source => {
    if (source.id === DATA_SOURCE_IDS.ADDITIONAL_DATA) {
      return includeCallerData;
    }
    if (resource.kind === 'alert') {
      return (
        source.id !== DATA_SOURCE_IDS.CALL_SUMMARY &&
        source.id !== DATA_SOURCE_IDS.CALL_TRANSCRIPT
      );
    }
    return true;
  });
};

export const createInitialValues = (
  sharingMethod: FieldSharingMethod,
  resource: ShareResource = CALL_RESOURCE,
  dataSourceOptions: DataSourceOptions = {},
): FieldFormValues => ({
  recipients: [],
  sharingMethod,
  dataSources: getDataSourcesForResource(resource, dataSourceOptions).map(
    source => ({
      ...source,
      isChecked: resource.kind === 'resource_request',
    }),
  ),
  notes: '',
  iarSelectedMembers: [],
});

// Step validation logic
export const isStepValid = (
  step: StepId,
  values: FieldFormValues,
  errors: FormikErrors<FieldFormValues>,
  sharingMethod: FieldSharingMethod,
): boolean => {
  switch (step) {
    case SHARE_STEP.RECIPIENTS:
      // Check for Formik errors first
      if (errors.recipients || errors.sharingMethod) {
        return false;
      }

      // For direct recipients, also check that we have valid recipients
      if (sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE) {
        return (
          values.recipients.length > 0 &&
          values.recipients.every(recipient => recipient.isValid)
        );
      }

      // For share-link and IaR, no recipients needed on the first step
      return true;
    case SHARE_STEP.IAR_SELECT:
      return values.iarSelectedMembers.length > 0;
    case SHARE_STEP.DATA_SOURCES:
      // Check for Formik errors first
      if (errors.dataSources) {
        return false;
      }

      // Also check that at least one data source is selected
      return values.dataSources.some(source => source.isChecked);
    case SHARE_STEP.NOTES:
      return true; // Notes are optional
    case SHARE_STEP.SHARE_LINK:
      return true; // Share link display step
    default:
      return false;
  }
};

const hasCoordinates = (
  loc: unknown,
): loc is { latitude: number; longitude: number } => {
  const obj = loc as Record<string, unknown>;
  return obj?.latitude != null && obj?.longitude != null;
};

/**
 * The alert fields forwarded in the share payload — the subset of the alert
 * record `buildAlertContext` copies across. `alert_id` is required because the
 * builder emits nothing without it.
 */
export type AlertSharePayload = Pick<
  SelectedAlert,
  | 'emergency_type'
  | 'site_type'
  | 'service_provider_name'
  | 'subtype'
  | 'status'
  | 'location'
  | 'location_history'
  | 'incident_time'
> & { alert_id: string };

/**
 * Resource-specific top-level payload fields. Both builders return this shape so
 * the spread into the submission payload has a single, predictable type.
 */
export interface ResourceContext {
  call?: { ani: string };
  alert?: AlertSharePayload;
  spatial_layer?: { latitude: number; longitude: number };
  additional_data?: unknown;
  resource_request?: ResourceRequestSharePayload;
}

const buildCallContext = (
  selectedCall: EmergencyCall | undefined,
  checkedSourceIds: Set<string>,
): ResourceContext => {
  if (!selectedCall) return {};

  // `core.locations` is append-ordered, so index 0 is the *first* fix of the
  // call — using it would freeze the shared location at wherever the caller was
  // when the call started. This picks the latest entry that actually carries
  // coordinates (primary or secondary), matching what the map and the Most
  // Recent Location card show.
  const location = getMostRecentGeoLocation(selectedCall);

  return {
    // Location-derived fields back the always-on map pin, so they are gated by
    // data presence rather than a checkbox.
    ...(location?.caller_id ? { call: { ani: location.caller_id } } : {}),
    ...(hasCoordinates(location)
      ? {
          spatial_layer: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
        }
      : {}),
    // Caller Data is only shared when its source is checked — unchecking it
    // must keep additional_data out of the payload.
    ...(checkedSourceIds.has(DATA_SOURCE_IDS.ADDITIONAL_DATA) &&
    selectedCall.additionalData
      ? { additional_data: selectedCall.additionalData }
      : {}),
  };
};

/**
 * Human label for an alert in the share confirmation toast — alerts have no
 * phone number to format. Falls back to the alert id when unnamed.
 */
export const getAlertShareLabel = (alert?: SelectedAlert): string | null => {
  if (!alert) return null;
  // `emergency_type` is a taxonomy object, not a string — joining it directly
  // renders "[object Object]" in the toast.
  const emergencyType =
    alert.emergency_type?.display_name ?? alert.emergency_type?.name;
  const parts = [emergencyType, alert.service_provider_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : (alert.alert_id ?? null);
};

/**
 * Builds the share-session URI for the resource being shared. Alerts use
 * `alert://{alert_id}` (the id already carries the `alert-` prefix, so this is
 * `alert://alert-<UUID>` — the form the backend validates); calls use
 * `tel://{callerId}`; resource requests `resource-request://{integration}/{id}`.
 * Returns '' when the relevant id is missing.
 */
export const buildShareUri = (
  resource: ShareResource,
  ids: {
    selectedCallerID?: string | null;
    alertId?: string | null;
    resourceRequestId?: number | null;
  },
): string => {
  if (resource.kind === 'alert') {
    return ids.alertId ? `alert://${ids.alertId}` : '';
  }
  if (resource.kind === 'resource_request') {
    return ids.resourceRequestId != null
      ? `resource-request://${resource.integration}/${ids.resourceRequestId}`
      : '';
  }
  return ids.selectedCallerID ? `tel://${ids.selectedCallerID}` : '';
};

export interface ResourceRequestShareContext {
  request?: NorfolkSouthernRequestResponse;
  eccName?: string;
}

/**
 * Share links are public, so only the fields Connect Share renders are sent,
 * named one by one so a new request field is never shared by accident.
 */
export const buildResourceRequestSharePayload = (
  request: NorfolkSouthernRequestResponse,
  eccName: string,
): ResourceRequestSharePayload => {
  const {
    train,
    consist,
    consistState,
    error,
    phoneNumber,
    reason,
    requestedAt,
    requester,
  } = request.request;
  return {
    integration: request.integration,
    request_id: request.id,
    ecc_name: eccName,
    data: {
      train,
      consist,
      consistState,
      error,
      phoneNumber,
      reason,
      requestedAt,
      requester: {
        firstName: requester.firstName,
        lastName: requester.lastName,
      },
    },
  };
};

/** Alert-share inputs resolved by the caller (see `useCurrentAdrData`). */
export interface AlertShareContext {
  selectedAlert?: SelectedAlert;
  /** ADR blob for this alert — `useCurrentAdrData(alert)?.data`. */
  additionalData?: unknown;
}

/**
 * Alert equivalent of `buildCallContext`. Emits `alert` instead of `call`, takes
 * coordinates from the alert's geodetic location, and sources additional_data
 * from the ADR slice rather than the call record.
 */
const buildAlertContext = (
  { selectedAlert, additionalData }: AlertShareContext,
  checkedSourceIds: Set<string>,
): ResourceContext => {
  if (!selectedAlert?.alert_id) return {};

  const geodetic = selectedAlert.location?.geodetic;

  return {
    alert: {
      alert_id: selectedAlert.alert_id,
      ...(selectedAlert.emergency_type
        ? { emergency_type: selectedAlert.emergency_type }
        : {}),
      ...(selectedAlert.site_type
        ? { site_type: selectedAlert.site_type }
        : {}),
      ...(selectedAlert.service_provider_name
        ? { service_provider_name: selectedAlert.service_provider_name }
        : {}),
      ...(selectedAlert.subtype ? { subtype: selectedAlert.subtype } : {}),
      ...(selectedAlert.status ? { status: selectedAlert.status } : {}),
      // Connect Share's Location card reads `location` for the civic address
      // and `provided_location`, and its map layer pans off
      // `location_history[0]`. `spatial_layer` below only carries bare
      // coordinates, so without these two the recipient gets a pin and an
      // otherwise-empty Location card.
      ...(selectedAlert.location ? { location: selectedAlert.location } : {}),
      ...(selectedAlert.location_history
        ? { location_history: selectedAlert.location_history }
        : {}),
      ...(selectedAlert.incident_time
        ? { incident_time: selectedAlert.incident_time }
        : {}),
    },
    // Same rule as calls: location backs the always-on map pin, so it is gated
    // by data presence rather than a checkbox.
    ...(hasCoordinates(geodetic)
      ? {
          spatial_layer: {
            latitude: geodetic.latitude,
            longitude: geodetic.longitude,
          },
        }
      : {}),
    // Intentionally an empty object, not the alert's ADR. Legend resolves
    // additional data for the alert on the backend, so the recipient does not
    // depend on the snapshot the sender ships. An empty dict is still required
    // because the create-session endpoint types `additional_data` as a dict and
    // rejects the flat array the alert ADR slice holds. Do not "fix" this by
    // passing `additionalData` through — that reintroduces the 400.
    ...(checkedSourceIds.has(DATA_SOURCE_IDS.ADDITIONAL_DATA) && additionalData
      ? { additional_data: {} }
      : {}),
  };
};

/** Dispatches to the context builder for the resource kind. */
const buildResourceContext = (
  resource: ShareResource,
  checkedSourceIds: Set<string>,
  selectedCall?: EmergencyCall,
  alertContext?: AlertShareContext,
  resourceRequestContext?: ResourceRequestShareContext,
): ResourceContext => {
  if (resource.kind === 'alert') {
    return buildAlertContext(alertContext ?? {}, checkedSourceIds);
  }
  if (resource.kind === 'resource_request') {
    const { request, eccName = '' } = resourceRequestContext ?? {};
    return request && checkedSourceIds.has(DATA_SOURCE_IDS.RESOURCE_REQUEST)
      ? { resource_request: buildResourceRequestSharePayload(request, eccName) }
      : {};
  }
  return buildCallContext(selectedCall, checkedSourceIds);
};

/**
 * Maps the latest call summary from the transcription slice to the shared
 * payload shape. Returns undefined when no summary has been generated yet so
 * the field is omitted rather than sent empty.
 */
export const buildCallSummaryPayload = (
  transcription?: TranscriptionStateCall,
): CallSummaryPayload | undefined => {
  const latest = transcription?.summaries?.[LATEST_SUMMARY_INDEX];
  if (!latest?.summary) return undefined;
  return {
    summary: latest.summary,
    keywords: latest.keywords ?? [],
    is_final: latest.is_final,
  };
};

/**
 * Maps the call transcript tokens + audio status from the transcription slice
 * to the shared payload shape. Returns undefined when there are no tokens yet.
 */
export const buildCallTranscriptPayload = (
  transcription?: TranscriptionStateCall,
): CallTranscriptPayload | undefined => {
  const payload = transcription?.payload;
  if (!payload || !payload.tokens?.length) return undefined;
  return {
    tokens: payload.tokens.map(token => ({
      token_id: token.token_id,
      speaker_display_name: token.speaker_display_name,
      transcript: token.transcript,
      start_time: token.start_time,
      end_time: token.end_time,
    })),
    call_information: payload.call_information,
  };
};

/** Undefined leaves the backend default lifetime in place. */
export const getShareExpirySeconds = (
  resource: ShareResource,
  sharingMethod: FieldSharingMethod,
): number | undefined => {
  if (resource.kind === 'resource_request') {
    return CONSIST_SHARE_EXPIRY_SECONDS;
  }
  return sharingMethod === FIELD_IAR_SHARE ? SHARE_EXPIRY_SECONDS : undefined;
};

export const createSubmissionPayload = (
  values: FieldFormValues,
  sharingMethod: FieldSharingMethod,
  uri: string,
  organization: string,
  selectedCall?: EmergencyCall,
  transcription?: TranscriptionStateCall,
  resource: ShareResource = CALL_RESOURCE,
  alertContext?: AlertShareContext,
  resourceRequestContext?: ResourceRequestShareContext,
) => {
  const isIar = sharingMethod === FIELD_IAR_SHARE;
  const expiry = getShareExpirySeconds(resource, sharingMethod);
  const isDirectRecipients = sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE;

  const checkedSources = values.dataSources.filter(source => source.isChecked);
  const checkedSourceIds = new Set(checkedSources.map(source => source.id));

  const callContext = buildResourceContext(
    resource,
    checkedSourceIds,
    selectedCall,
    alertContext,
    resourceRequestContext,
  );

  // Alerts have no transcription, so these stay undefined on the alert branch
  // (their data sources are not offered for alerts either).
  const callSummary = checkedSourceIds.has(CALL_SUMMARY_SOURCE_ID)
    ? buildCallSummaryPayload(transcription)
    : undefined;
  const callTranscript = checkedSourceIds.has(CALL_TRANSCRIPT_SOURCE_ID)
    ? buildCallTranscriptPayload(transcription)
    : undefined;

  // Call and alert shares send the map entry in display_config so the Connect
  // Share view always shows the location pin alongside any selected data
  // sources. (IaR additionally attaches a `members` array — see below.)
  const displayConfig: DisplayConfigItem[] = [
    ...checkedSources.map(source => ({ name: source.id, meta: {} })),
    ...(resource.kind === 'resource_request' ? [] : MAP_DISPLAY_CONFIG),
  ];

  // Only direct-recipients shares carry recipient emails; link and IaR shares
  // leave them empty. Recipients are email-only — phone/SMS delivery was removed.
  const emails: string[] = [];
  if (isDirectRecipients) {
    values.recipients.forEach(recipient => {
      if (isValidEmail(recipient.value)) {
        emails.push(recipient.value);
      }
    });
  }

  return {
    uri,
    organization,
    ...(expiry !== undefined ? { expiry } : {}),
    recipients: {
      emails,
      phone_numbers: [],
      organization_contacts: [],
      contact_groups: [],
    },
    ...(isIar ? { members: values.iarSelectedMembers } : {}),
    display_config: displayConfig,
    additional_comments: values.notes ? [values.notes] : [],
    ...callContext,
    ...(callSummary ? { call_summary: callSummary } : {}),
    ...(callTranscript ? { call_transcript: callTranscript } : {}),
  };
};
